<template>
  <div />
</template>

<script>
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import MapboxDraw from '@mapbox/mapbox-gl-draw'
import { area } from '@turf/turf'
import { mapGetters, mapMutations, mapState } from 'vuex'
import { RESET_CURRENT_MEASUREMENT, SET_CURRENT_MEASUREMENT } from '@/store/mutation-types'

export default {
  name: 'LayerMeasureArea',
  data() {
    return {
      draw: new MapboxDraw({
        displayControlsDefault: false,
        defaultMode: 'draw_polygon',
        styles: [
          {
            id: 'gl-draw-polygon-fill-inactive',
            type: 'fill',
            filter: [
              'all',
              ['==', 'active', 'false'],
              ['==', '$type', 'Polygon'],
              ['!=', 'mode', 'static']
            ],
            paint: {
              'fill-color': '#80b5b4',
              'fill-outline-color': '#80b5b4',
              'fill-opacity': 0.3
            }
          },
          {
            id: 'gl-draw-polygon-fill-active',
            type: 'fill',
            filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
            paint: {
              'fill-color': '#80b5b4',
              'fill-outline-color': '#80b5b4',
              'fill-opacity': 0.6
            }
          },
          {
            id: 'gl-draw-polygon-midpoint',
            type: 'circle',
            filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
            paint: {
              'circle-radius': 3.5,
              'circle-color': '#80b5b4'
            }
          },
          {
            id: 'gl-draw-polygon-stroke-inactive',
            type: 'line',
            filter: [
              'all',
              ['==', 'active', 'false'],
              ['==', '$type', 'Polygon'],
              ['!=', 'mode', 'static']
            ],
            layout: {
              'line-cap': 'round',
              'line-join': 'round'
            },
            paint: {
              'line-color': '#80b5b4',
              'line-width': 2.5
            }
          },
          {
            id: 'gl-draw-polygon-stroke-active',
            type: 'line',
            filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
            layout: {
              'line-cap': 'round',
              'line-join': 'round'
            },
            paint: {
              'line-color': '#80b5b4',
              'line-dasharray': [0.2, 2],
              'line-width': 2.5
            }
          },
          {
            id: 'gl-draw-line-inactive',
            type: 'line',
            filter: [
              'all',
              ['==', 'active', 'false'],
              ['==', '$type', 'LineString'],
              ['!=', 'mode', 'static']
            ],
            layout: {
              'line-cap': 'round',
              'line-join': 'round'
            },
            paint: {
              'line-color': '#80b5b4',
              'line-width': 2.5
            }
          },
          {
            id: 'gl-draw-line-active',
            type: 'line',
            filter: ['all', ['==', '$type', 'LineString'], ['==', 'active', 'true']],
            layout: {
              'line-cap': 'round',
              'line-join': 'round'
            },
            paint: {
              'line-color': '#80b5b4',
              'line-dasharray': [0.2, 2],
              'line-width': 2.5
            }
          },
          {
            id: 'gl-draw-polygon-and-line-vertex-stroke-inactive',
            type: 'circle',
            filter: [
              'all',
              ['==', 'meta', 'vertex'],
              ['==', '$type', 'Point'],
              ['!=', 'mode', 'static']
            ],
            paint: {
              'circle-radius': 5, // Halo hidden
              'circle-color': '#fff'
            }
          },
          {
            id: 'gl-draw-polygon-and-line-vertex-inactive',
            type: 'circle',
            filter: [
              'all',
              ['==', 'meta', 'vertex'],
              ['==', '$type', 'Point'],
              ['!=', 'mode', 'static']
            ],
            paint: {
              'circle-radius': 5,
              'circle-color': '#80b5b4'
            }
          },
          {
            id: 'gl-draw-point-point-stroke-inactive',
            type: 'circle',
            filter: [
              'all',
              ['==', 'active', 'false'],
              ['==', '$type', 'Point'],
              ['==', 'meta', 'feature'],
              ['!=', 'mode', 'static']
            ],
            paint: {
              'circle-radius': 5, // Halo hidden
              'circle-opacity': 1,
              'circle-color': '#fff'
            }
          },
          {
            id: 'gl-draw-point-inactive',
            type: 'circle',
            filter: [
              'all',
              ['==', 'active', 'false'],
              ['==', '$type', 'Point'],
              ['==', 'meta', 'feature'],
              ['!=', 'mode', 'static']
            ],
            paint: {
              'circle-radius': 5,
              'circle-color': '#80b5b4'
            }
          },
          {
            id: 'gl-draw-point-stroke-active',
            type: 'circle',
            filter: [
              'all',
              ['==', '$type', 'Point'],
              ['==', 'active', 'true'],
              ['!=', 'meta', 'midpoint']
            ],
            paint: {
              'circle-radius': 5, // Halo hidden
              'circle-color': '#fff'
            }
          },
          {
            id: 'gl-draw-point-active',
            type: 'circle',
            filter: [
              'all',
              ['==', '$type', 'Point'],
              ['!=', 'meta', 'midpoint'],
              ['==', 'active', 'true']
            ],
            paint: {
              'circle-radius': 5,
              'circle-color': '#80b5b4'
            }
          },
          {
            id: 'gl-draw-polygon-fill-static',
            type: 'fill',
            filter: ['all', ['==', 'mode', 'static'], ['==', '$type', 'Polygon']],
            paint: {
              'fill-color': '#80b5b4',
              'fill-outline-color': '#80b5b4',
              'fill-opacity': 0.2
            }
          },
          {
            id: 'gl-draw-polygon-stroke-static',
            type: 'line',
            filter: ['all', ['==', 'mode', 'static'], ['==', '$type', 'Polygon']],
            layout: {
              'line-cap': 'round',
              'line-join': 'round'
            },
            paint: {
              'line-color': '#80b5b4',
              'line-width': 2.5
            }
          },
          {
            id: 'gl-draw-line-static',
            type: 'line',
            filter: ['all', ['==', 'mode', 'static'], ['==', '$type', 'LineString']],
            layout: {
              'line-cap': 'round',
              'line-join': 'round'
            },
            paint: {
              'line-color': '#80b5b4',
              'line-width': 2.5
            }
          },
          {
            id: 'gl-draw-point-static',
            type: 'circle',
            filter: ['all', ['==', 'mode', 'static'], ['==', '$type', 'Point']],
            paint: {
              'circle-radius': 5,
              'circle-color': '#80b5b4'
            }
          }
        ]
      }),
      turfArea: area
    }
  },
  computed: {
    ...mapGetters({
      isMeasurementActive: 'isMeasurementActive'
    }),
    ...mapState({
      currentTool: (state) => state.currentTool
    })
  },
  watch: {
    currentTool: {
      immediate: true,
      handler(newTool, old) {
        if (this.isMeasurementActive && newTool === 'areameasurement') {
          // Enter (or switch to) area measurement
          this.$store.map.addControl(this.draw)
          this.$store.map.on('draw.create', this.updateArea)
          this.$store.map.on('draw.delete', this.updateArea)
          this.$store.map.on('draw.update', this.updateArea)
          this.setCursor('crosshair')
        } else if (old === 'areameasurement') {
          // Switch to line measurement (area measurement is turned off)
          this.$store.map.removeControl(this.draw)
        }
      }
    }
  },
  beforeUnmount() {
    this.$store.map.off('draw.create', this.updateArea)
    this.$store.map.off('draw.delete', this.updateArea)
    this.$store.map.off('draw.update', this.updateArea)
    this.resetCurrentMeasurement()
  },
  methods: {
    ...mapMutations({
      resetCurrentMeasurement: RESET_CURRENT_MEASUREMENT,
      setCurrentMeasurement: SET_CURRENT_MEASUREMENT
    }),
    setCursor(cursorType) {
      this.$store.map.getCanvas().style.cursor = cursorType
    },
    updateArea() {
      const data = this.draw.getAll()
      if (data.features.length) {
        this.setCurrentMeasurement({ currentMeasurement: this.turfArea(data) })
        return
      }
      this.resetCurrentMeasurement()
    }
  }
}
</script>
